import { useTranslations } from '@vocab/react';
import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useFormContext, type UseFormSetValue } from 'react-hook-form';

import { VirusScanStatus } from 'src/__generated__/graphql';
import { COVER_LETTER_KEY } from 'src/constants/app';
import { PageSection, useTrackingClient } from 'src/context/tracking';
import { uploadCoverLetter } from 'src/context/upload-attachments';
import { usePromises } from 'src/context/use-promises';
import {
  AttachmentOptionFile,
  AttachmentOptionText,
  AttachmentSelect,
  FileAttachment,
} from 'src/forms/common/AttachmentSelect/AttachmentSelect';
import {
  MAX_CHARS,
  useValidators,
} from 'src/forms/common/AttachmentSelect/hooks/useValidators';
import { useGetCurrentPage } from 'src/hooks/useGetCurrentPage';
import { useVirusScan } from 'src/hooks/useVirusScan';
import { isFile } from 'src/utils';

import translations from './.vocab';

export interface CoverLetterFieldsValues {
  'coverLetter-method': FileAttachment;
  'coverLetter-uri': string;
  'coverLetter-file': File;
  'coverLetter-text': string;
  'coverLetter-virusScan': unknown;
}

interface Props {
  defaultMethod?: FileAttachment;
  defaultValue?: string | File;
  hideTitle?: boolean;
  SetValueOverride?: UseFormSetValue<CoverLetterFieldsValues>;
  isLoadingOverride?: boolean;
  isVirusScanEnabled?: boolean;
}

export function CoverLetterFields({
  defaultMethod,
  defaultValue,
  hideTitle = false,
  SetValueOverride,
  isLoadingOverride = false,
  isVirusScanEnabled = true,
}: Props) {
  const { t } = useTranslations(translations);
  const { trackDontIncludeCoverletter } = useTracking();
  const { addPromise } = usePromises();
  const [isLoading, setIsLoading] = useState(isLoadingOverride);

  const { watch, setValue, trigger, formState, setError, clearErrors } =
    useFormContext<CoverLetterFieldsValues>();

  const { validateMethod, validateFile, validateText } = useValidators({
    currentMethod: watch('coverLetter-method') || defaultMethod,
  });

  useVirusScan({
    fileUri: isVirusScanEnabled ? watch('coverLetter-uri') : undefined,
    currentMethod: watch('coverLetter-method'),
    onStatusChange: (virusScanStatus, method) => {
      if (!isVirusScanEnabled) {
        return;
      }

      const shouldCheck = method === FileAttachment.UPLOAD;

      if (!shouldCheck) {
        clearErrors('coverLetter-virusScan');
      } else if (virusScanStatus === VirusScanStatus.HasVirus) {
        setError('coverLetter-virusScan', { message: t('Virus Detected') });
      } else {
        clearErrors('coverLetter-virusScan');
      }

      setIsLoading(virusScanStatus === VirusScanStatus.Pending);
    },
  });

  useEffect(() => {
    setIsLoading(isLoadingOverride);
  }, [isLoadingOverride]);

  const handleCoverLetterChange = (coverLetter?: File) => {
    if (!coverLetter) {
      return;
    }
    setIsLoading(true);

    addPromise(COVER_LETTER_KEY, uploadCoverLetter({ coverLetter }), (res) => {
      if (res) {
        const setVal = SetValueOverride || setValue;
        flushSync(() => setVal('coverLetter-uri', res));
      }
      trigger('coverLetter-file');
    });
  };

  return (
    <AttachmentSelect
      hideTitle={hideTitle}
      title={t('Cover letter')}
      name="coverLetter-method"
      labelNone={t('Dont include a cover letter')}
      defaultValue={defaultMethod}
      onChange={(method) => {
        if (method === FileAttachment.NONE) {
          trackDontIncludeCoverletter();
        }
      }}
      validate={validateMethod}
    >
      <AttachmentOptionFile
        testid="coverLetterFileInput"
        name="coverLetter-file"
        label={t('Upload a cover letter')}
        description={t('Accepted file types')}
        reUploadText={t('Upload a different cover letter')}
        defaultValue={isFile(defaultValue) ? defaultValue : undefined}
        validate={validateFile}
        onChange={handleCoverLetterChange}
        isLoading={isLoading}
        message={formState.errors['coverLetter-virusScan']?.message}
      />
      <AttachmentOptionText
        testid="coverLetterTextInput"
        name="coverLetter-text"
        label={t('Write a cover letter')}
        description={t('Introduce yourself')}
        defaultValue={
          typeof defaultValue === 'string' ? defaultValue : undefined
        }
        validate={validateText}
        limit={MAX_CHARS}
      />
    </AttachmentSelect>
  );
}

function useTracking() {
  const currentPage = useGetCurrentPage();
  const props = { currentPage };
  const client = useTrackingClient();

  function trackDontIncludeCoverletter() {
    client.trackLink({
      ...props,
      link: 'cover_letter_not_included_pressed',
      eventName: 'cover_letter_not_included_pressed',
      currentPageSection: PageSection.COVER_LETTER,
    });
  }

  return {
    trackDontIncludeCoverletter,
  };
}
